import { useState } from 'react';
import { ChevronDown, Layers } from 'lucide-react';
import type { DashboardOverview } from '@skhub/contracts';
import { useDashboard } from '@/lib/queries';
import { num, pct } from '@/lib/format';
import { useCountUp } from '@/lib/motion';
import { statusColor } from '@/lib/status';
import { CategoryIcon, Skeleton } from '@/components/ui';

type Cat = DashboardOverview['byCategory'][number];
const VISIBLE = 9;

/**
 * Fleet composition: every category as a tile (count, share, status mix), biggest first. Selecting a tile
 * filters the register below; selecting it again clears the filter. All numbers come from the live register.
 */
export function CategoryDeck({ selected, onSelect }: { selected: string; onSelect: (category: string | null) => void }) {
  const q = useDashboard();
  const [expanded, setExpanded] = useState(false);
  const d = q.data;
  const cats = d ? [...d.byCategory].filter((c) => c.total > 0).sort((a, b) => b.total - a.total) : undefined;
  const total = cats?.reduce((s, c) => s + c.total, 0);
  const shownTotal = useCountUp(total);
  const lead = cats?.[0];
  const rest = cats?.slice(1) ?? [];
  const shown = expanded ? rest : rest.slice(0, VISIBLE - 1);

  return (
    <section aria-labelledby="fleet-heading" className="deck scope-dark mb-5 p-5 text-white sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.2em] text-[#ff8a57] uppercase">
            <Layers aria-hidden className="size-3.5" /> Fleet composition
          </p>
          <h2 id="fleet-heading" className="mt-2 text-[22px] leading-tight font-semibold tracking-[-0.02em]">
            {cats ? (
              <>
                <span className="tnum">{num(shownTotal ?? total ?? 0)}</span> devices across <span className="tnum">{num(cats.length)}</span> categories
              </>
            ) : (
              <Skeleton className="h-7 w-72 opacity-40" />
            )}
          </h2>
          <p className="mt-1 text-[13px] text-white/55">Select a category to list only those devices.</p>
        </div>
        {selected && (
          <button onClick={() => onSelect(null)} className="btn btn-glass btn-sm">
            Show all categories
          </button>
        )}
      </div>

      {/* Share of the fleet, one segment per category (largest first). */}
      {cats && total ? (
        <div aria-hidden className="animate-grow-x mt-5 flex h-1.5 gap-[2px] overflow-hidden rounded-full">
          {cats.map((c, i) => (
            <span key={c.category} title={`${c.category}: ${c.total}`} style={{ flexGrow: c.total, background: segment(i), opacity: selected && selected !== c.category ? 0.25 : 1 }} className="transition-opacity" />
          ))}
        </div>
      ) : (
        <Skeleton className="mt-5 h-1.5 opacity-30" />
      )}

      <ul className="stagger mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4" aria-label="Devices by category">
        {!cats
          ? Array.from({ length: 10 }, (_, i) => (
              <li key={i} className={i === 0 ? 'col-span-2 row-span-2' : ''}>
                <div className="glass h-full rounded-[14px] p-4">
                  <Skeleton className="h-3 w-20 opacity-40" />
                  <Skeleton className="mt-3 h-7 w-12 opacity-40" />
                </div>
              </li>
            ))
          : [lead!, ...shown].map((c, i) => (
              <li key={c.category} style={{ ['--i' as string]: i }} className={i === 0 ? 'col-span-2 row-span-2' : ''}>
                <Tile c={c} total={total!} lead={i === 0} color={segment(i)} selected={selected === c.category} dimmed={!!selected && selected !== c.category} onSelect={onSelect} />
              </li>
            ))}
      </ul>

      {cats && rest.length > VISIBLE - 1 && (
        <button onClick={() => setExpanded((v) => !v)} aria-expanded={expanded} className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-white/65 hover:text-white">
          {expanded ? 'Show fewer categories' : `Show all ${num(cats.length)} categories`}
          <ChevronDown aria-hidden className={`size-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
        </button>
      )}
    </section>
  );
}

/** Categorical palette for the share bar and tile accents: brand first, then calm distinct hues. */
const PALETTE = ['#ff8a57', '#6aa7f0', '#3ccf9f', '#a990f2', '#e48aab', '#e5a54a', '#5fc4d6', '#9aa7b6'];
const segment = (i: number) => PALETTE[Math.min(i, PALETTE.length - 1)]!;

function Tile({ c, total, lead, color, selected, dimmed, onSelect }: { c: Cat; total: number; lead: boolean; color: string; selected: boolean; dimmed: boolean; onSelect: (v: string | null) => void }) {
  const shown = useCountUp(c.total);
  const statuses = c.byStatus.filter((s) => s.count > 0);
  return (
    <button
      onClick={() => onSelect(selected ? null : c.category)}
      aria-pressed={selected}
      className={`fleet-tile glass group relative flex h-full w-full flex-col overflow-hidden rounded-[14px] text-left ${lead ? 'p-5' : 'p-3.5'} ${selected ? 'is-selected' : ''} ${dimmed ? 'opacity-55 hover:opacity-100' : ''}`}
      style={{ ['--accent' as string]: color }}
    >
      <span aria-hidden className="fleet-glow" />
      <span className="flex items-center gap-2.5">
        <span className={`grid shrink-0 place-items-center rounded-[10px] border border-white/10 bg-white/[0.05] ${lead ? 'size-10' : 'size-8'}`} style={{ color }}>
          <CategoryIcon type={c.category} className={lead ? 'size-5' : 'size-4'} />
        </span>
        <span className={`min-w-0 truncate font-medium text-white/85 ${lead ? 'text-[14px]' : 'text-[12.5px]'}`} title={c.category}>
          {c.category}
        </span>
      </span>
      <span className={`mt-auto flex items-end justify-between gap-2 ${lead ? 'pt-6' : 'pt-3'}`}>
        <span className={`leading-none font-semibold tracking-[-0.03em] text-white tnum ${lead ? 'text-[40px]' : 'text-[24px]'}`}>{num(shown ?? c.total)}</span>
        <span className="pb-0.5 text-[11px] text-white/50 tnum">{pct(c.total / total, c.total / total < 0.01 ? 1 : 0)}</span>
      </span>
      <span aria-hidden className={`mt-2.5 flex overflow-hidden rounded-full bg-white/[0.06] ${lead ? 'h-2' : 'h-1'}`}>
        {statuses.map((s) => (
          <span key={s.key} className="animate-grow-x h-full" style={{ flexGrow: s.count, background: statusColor(s.key) }} />
        ))}
      </span>
      {lead && (
        <span className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
          {statuses.map((s) => (
            <span key={s.key} className="flex items-center justify-between gap-2 text-white/65">
              <span className="flex items-center gap-1.5">
                <span aria-hidden className="size-1.5 rounded-full" style={{ background: statusColor(s.key) }} />
                {s.key}
              </span>
              <span className="font-medium text-white tnum">{num(s.count)}</span>
            </span>
          ))}
        </span>
      )}
      <span className="sr-only">
        {statuses.map((s) => `${s.count} ${s.key}`).join(', ')}
      </span>
    </button>
  );
}
